/**
 * VoiceTestMode Component
 * ========================
 * In-browser voice testing for Voice Booking Agent
 * 
 * PHASE 2: OpenAI Realtime API (WebRTC) - low latency, barge-in support
 * PHASE 1 FALLBACK: OpenAI TTS + Web Speech API STT
 * 
 * For admin/testing only - does NOT require Twilio
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';
import RealtimeVoiceSession from './RealtimeVoiceSession';

import {
  ArrowCounterClockwise,
  Check,
  CircleNotch,
  GearSix,
  Lightning,
  Microphone,
  MicrophoneSlash,
  PencilSimple,
  Phone,
  PhoneDisconnect,
  SpeakerHigh,
  SpeakerX,
  WarningCircle,
  WifiHigh,
  WifiSlash,
  X
} from '@phosphor-icons/react';
// Check for Web Speech API support
const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const browserSpeechSynthesis = window.speechSynthesis;

// Voice mode options
const VOICE_MODE = {
  REALTIME: 'realtime',  // Phase 2: OpenAI Realtime API (WebRTC)
  STREAMING: 'streaming' // Phase 1: OpenAI TTS + Web Speech API
};

// OpenAI TTS voices (for Streaming mode)
const OPENAI_TTS_VOICES = [
  { id: 'alloy', name: 'Alloy', description: 'Neutral, balanced' },
  { id: 'echo', name: 'Echo', description: 'Deep, warm' },
  { id: 'fable', name: 'Fable', description: 'Expressive, dramatic' },
  { id: 'onyx', name: 'Onyx', description: 'Deep, authoritative' },
  { id: 'nova', name: 'Nova', description: 'Friendly, upbeat' },
  { id: 'shimmer', name: 'Shimmer', description: 'Warm, gentle' }
];

// OpenAI Realtime voices (different from TTS!)
const OPENAI_REALTIME_VOICES = [
  { id: 'alloy', name: 'Alloy', description: 'Neutral, balanced' },
  { id: 'ash', name: 'Ash', description: 'Calm, thoughtful' },
  { id: 'ballad', name: 'Ballad', description: 'Melodic, expressive' },
  { id: 'coral', name: 'Coral', description: 'Warm, friendly' },
  { id: 'echo', name: 'Echo', description: 'Deep, resonant' },
  { id: 'sage', name: 'Sage', description: 'Wise, measured' },
  { id: 'shimmer', name: 'Shimmer', description: 'Bright, energetic' },
  { id: 'verse', name: 'Verse', description: 'Clear, articulate' }
];

// Combined voices list for display (uses common voices)
const OPENAI_VOICES = OPENAI_TTS_VOICES;

const VoiceTestMode = ({ agentId, agentName, businessName, businessDescription, customKnowledge, openingMessage, requiredBookingInfo, onClose }) => {
  const { activeOrganization } = useOrganization();
  
  // Voice mode state (Realtime vs Streaming)
  const [voiceMode, setVoiceMode] = useState(VOICE_MODE.REALTIME);
  const [realtimeAvailable, setRealtimeAvailable] = useState(null); // null = checking, true/false = result
  const [realtimeError, setRealtimeError] = useState(null);
  const [realtimeKeySource, setRealtimeKeySource] = useState(null);
  
  // Call state
  const [callActive, setCallActive] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  
  // Speech recognition state
  const [isListening, setIsListening] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState('');
  
  // TTS state (configurable before call)
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [ttsProvider, setTtsProvider] = useState('openai'); // 'openai' or 'browser'
  const [openaiVoice, setOpenaiVoice] = useState('alloy'); // Default to 'alloy' (works in both TTS and Realtime)
  const [realtimeVoice, setRealtimeVoice] = useState('alloy'); // Separate voice for Realtime mode
  const [ttsSpeed, setTtsSpeed] = useState(1.0);
  const [ttsAvailable, setTtsAvailable] = useState(false);
  const [ttsKeySource, setTtsKeySource] = useState(null);
  
  // Realtime-specific settings
  const [pauseTolerance, setPauseTolerance] = useState(2000); // idle_timeout_ms - default 2.0s for natural pauses
  
  // Locked TTS settings (snapshot at call start - DO NOT change during call)
  const [lockedTtsSettings, setLockedTtsSettings] = useState(null);
  const [ttsFallbackUsed, setTtsFallbackUsed] = useState(false);
  
  // Per-utterance debug info (for debugging voice consistency)
  const [lastUtteranceDebug, setLastUtteranceDebug] = useState(null);
  
  // Browser voices (fallback)
  const [browserVoices, setBrowserVoices] = useState([]);
  const [selectedBrowserVoice, setSelectedBrowserVoice] = useState(null);
  
  // Conversation state
  const [messages, setMessages] = useState([]);
  const [threadId, setThreadId] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  
  // Welcome message state - use openingMessage from wizard if available
  const [welcomeMessage, setWelcomeMessage] = useState(
    openingMessage || `Hello! Thank you for calling ${businessName || 'our business'}. This is your AI booking assistant. How can I help you today?`
  );
  const [editingWelcome, setEditingWelcome] = useState(false);
  const [tempWelcomeMessage, setTempWelcomeMessage] = useState('');
  
  // GearSix
  const [showSettings, setShowSettings] = useState(true); // Default to open for better UX
  const [autoListen, setAutoListen] = useState(false); // Default to OFF to prevent speech recognition errors
  
  // Refs
  const recognitionRef = useRef(null);
  const callTimerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const audioRef = useRef(null);
  const transcriptRef = useRef('');
  const utteranceCountRef = useRef(0); // Track utterance count for debug
  const lockedTtsSettingsRef = useRef(null); // Keep ref in sync with state for closures

  // ═══════════════════════════════════════════════════════════════════════════
  // SANITIZATION: Remove <memory> and <action> tags from assistant output
  // ═══════════════════════════════════════════════════════════════════════════
  const sanitizeAssistantOutput = (text) => {
    if (!text) return '';
    
    // Remove <memory>...</memory> blocks (including multiline)
    let sanitized = text.replace(/<memory>[\s\S]*?<\/memory>/gi, '');
    
    // Remove <action>...</action> blocks (including multiline)
    sanitized = sanitized.replace(/<action>[\s\S]*?<\/action>/gi, '');
    
    // Remove any orphaned tags
    sanitized = sanitized.replace(/<\/?memory>/gi, '');
    sanitized = sanitized.replace(/<\/?action>/gi, '');
    
    // Clean up extra whitespace
    sanitized = sanitized.replace(/\n{3,}/g, '\n\n').trim();
    
    // If sanitization removed everything, use fallback message
    if (!sanitized || sanitized.length < 3) {
      return "All set — I'm processing that for you now.";
    }
    
    return sanitized;
  };

  // Update welcome message when businessName changes
  useEffect(() => {
    if (businessName && !editingWelcome) {
      setWelcomeMessage(`Hello! Thank you for calling ${businessName}. This is your AI booking assistant. How can I help you today?`);
    }
  }, [businessName, editingWelcome]);

  // Check Realtime API availability on mount (Phase 2)
  useEffect(() => {
    const checkRealtimeAvailability = async () => {
      try {
        console.log('[VoiceTestMode] Checking Realtime API availability...');
        const response = await fetchWithOrg(
          `${BACKEND_URL}/api/voice/realtime/test?agent_id=${agentId || ''}`,
          { credentials: 'include' },
          activeOrganization?.organization_id
        );
        
        if (response.ok) {
          const data = await response.json();
          setRealtimeAvailable(data.realtime_available);
          setRealtimeKeySource(data.key_source);
          
          if (data.realtime_available) {
            // Realtime available - set as default mode
            setVoiceMode(VOICE_MODE.REALTIME);
            console.log('[VoiceTestMode] Realtime API available, using as default');
          } else {
            // Realtime not available - use streaming fallback
            setRealtimeError(data.error);
            setVoiceMode(VOICE_MODE.STREAMING);
            console.log('[VoiceTestMode] Realtime unavailable:', data.error);
          }
        } else {
          setRealtimeAvailable(false);
          setVoiceMode(VOICE_MODE.STREAMING);
        }
      } catch (err) {
        console.warn('[VoiceTestMode] Realtime check failed:', err);
        setRealtimeAvailable(false);
        setVoiceMode(VOICE_MODE.STREAMING);
      }
    };
    
    checkRealtimeAvailability();
  }, [agentId, activeOrganization]);

  // Check TTS availability on mount (Phase 1 fallback)
  useEffect(() => {
    const checkTTS = async () => {
      try {
        const response = await fetchWithOrg(
          `${BACKEND_URL}/api/voice/tts/test?agent_id=${agentId || ''}`,
          { credentials: 'include' },
          activeOrganization?.organization_id
        );
        
        if (response.ok) {
          const data = await response.json();
          setTtsAvailable(data.tts_available);
          setTtsKeySource(data.key_source);
          
          if (!data.tts_available) {
            // Fall back to browser TTS
            setTtsProvider('browser');
          }
        }
      } catch (err) {
        console.warn('TTS check failed, using browser fallback:', err);
        setTtsProvider('browser');
      }
    };
    
    checkTTS();
  }, [agentId, activeOrganization]);

  // Load browser voices (fallback)
  useEffect(() => {
    const loadVoices = () => {
      const availableVoices = browserSpeechSynthesis?.getVoices() || [];
      setBrowserVoices(availableVoices);
      
      // Prefer a natural-sounding English voice
      const preferred = availableVoices.find(v => 
        v.name.includes('Samantha') || 
        v.name.includes('Karen') ||
        v.name.includes('Google UK English Female') ||
        v.name.includes('Microsoft Zira')
      ) || availableVoices.find(v => v.lang.startsWith('en'));
      
      if (preferred) setSelectedBrowserVoice(preferred);
    };
    
    loadVoices();
    if (browserSpeechSynthesis) {
      browserSpeechSynthesis.onvoiceschanged = loadVoices;
    }
    
    return () => {
      if (browserSpeechSynthesis) {
        browserSpeechSynthesis.onvoiceschanged = null;
      }
    };
  }, []);

  // Scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Call duration timer
  useEffect(() => {
    if (callActive) {
      callTimerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    } else {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
    }
    
    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
    };
  }, [callActive]);

  // Handle user message - defined before initRecognition
  const handleUserMessage = useCallback(async (text) => {
    if (!text.trim() || processing) return;
    
    // Add user message to conversation
    const userMessage = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setProcessing(true);
    setError(null);
    
    try {
      let currentThreadId = threadId;
      let responseData;
      
      // If no thread yet, create thread with first message
      if (!currentThreadId) {
        const response = await fetchWithOrg(
          `${BACKEND_URL}/api/agent/${agentId}/threads?mode=test`,  // Add test mode
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              message: text,
              mode: 'test',  // Also include in body for redundancy
              metadata: { source: 'voice_test_mode', channel: 'voice_test' }
            })
          },
          activeOrganization?.organization_id
        );
        
        responseData = await response.json();
        
        // Check for error in response body (backend returns success: false)
        if (responseData.success === false || responseData.error) {
          throw new Error(responseData.error || 'Failed to create thread');
        }
        
        currentThreadId = responseData.thread_id || responseData.id;
        if (currentThreadId) {
          setThreadId(currentThreadId);
        }
      } else {
        // Send message to existing thread
        const response = await fetchWithOrg(
          `${BACKEND_URL}/api/agent/threads/${currentThreadId}/message?mode=test`,  // Add test mode
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ 
              message: text,
              mode: 'test',  // Also include in body for redundancy
              metadata: { channel: 'voice_test' }
            })
          },
          activeOrganization?.organization_id
        );
        
        responseData = await response.json();
        
        // Check for error in response body
        if (responseData.success === false || responseData.error) {
          throw new Error(responseData.error || 'Failed to send message');
        }
      }
      
      // Extract response text - check multiple possible response shapes
      let responseText = null;
      
      // 1. Direct text fields (preferred, standardized)
      responseText = responseData.assistant_text || responseData.response || responseData.message || responseData.content || responseData.assistant_message;
      
      // 2. If not found, extract from messages array
      if (!responseText && responseData.messages && Array.isArray(responseData.messages)) {
        // Find the last assistant message
        const assistantMessages = responseData.messages.filter(msg => msg.role === 'assistant');
        if (assistantMessages.length > 0) {
          const lastAssistantMsg = assistantMessages[assistantMessages.length - 1];
          
          // Extract text from various content shapes
          if (typeof lastAssistantMsg.content === 'string') {
            responseText = lastAssistantMsg.content;
          } else if (lastAssistantMsg.text) {
            responseText = lastAssistantMsg.text;
          } else if (Array.isArray(lastAssistantMsg.content)) {
            // Handle content array format: [{type: 'text', text: '...'}]
            responseText = lastAssistantMsg.content
              .filter(part => part.type === 'text' && part.text)
              .map(part => part.text)
              .join('\n');
          }
        }
      }
      
      if (responseText) {
        // ═══════════════════════════════════════════════════════════════════════════
        // SANITIZE OUTPUT: Strip <memory>, <action> and other XML tags from LLM output
        // This ensures clean text for both display and TTS
        // ═══════════════════════════════════════════════════════════════════════════
        const sanitizedText = sanitizeAssistantOutput(responseText);
        console.log('[VoiceTestMode] Sanitized response:', { original: responseText.substring(0, 100), sanitized: sanitizedText.substring(0, 100) });
        
        // Add assistant message (with sanitized content)
        const assistantMessage = {
          role: 'assistant',
          content: sanitizedText,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, assistantMessage]);
        
        // Speak the sanitized response
        speakText(sanitizedText);
      } else {
        console.warn('No response text found in:', responseData);
        // Check if there's an error in the response
        if (responseData.error) {
          // Format runtime errors for better UX
          let errorMsg = responseData.error;
          if (errorMsg.includes("'role'") || errorMsg.includes("KeyError")) {
            errorMsg = "Message format error. The agent may need to be reconfigured.";
          }
          setError(errorMsg);
        } else if (responseData.status === 'failed') {
          setError('Agent execution failed. Check agent configuration.');
        } else if (responseData.success === true) {
          // Run completed but no assistant message found
          setError('Run completed but no assistant output returned. Check agent response format.');
        } else {
          setError('No response from agent. The agent may not be configured properly.');
        }
        resumeListening();
      }
      
    } catch (err) {
      console.error('Error in handleUserMessage:', err);
      // Format error message for better UX
      let errorMessage = err.message || 'Failed to get response from agent';
      
      // Make common errors more user-friendly
      if (errorMessage.includes('draft') || errorMessage.includes('Draft')) {
        errorMessage = 'Agent is in Draft mode. Test mode should allow this - please check backend logs.';
      } else if (errorMessage.includes('not found')) {
        errorMessage = 'Agent not found. Please verify the agent exists.';
      } else if (errorMessage.includes('active')) {
        errorMessage = 'Agent needs to be activated. Go to Agent Editor to activate.';
      } else if (errorMessage.includes("'role'") || errorMessage.includes("KeyError")) {
        errorMessage = 'Message format error in agent runtime. Check backend logs.';
      } else if (errorMessage.includes('Runtime error')) {
        // Extract the actual error from "Runtime error: X"
        errorMessage = errorMessage.replace('Runtime error: ', 'Agent error: ');
      }
      
      setError(errorMessage);
      // Resume listening on error
      resumeListening();
    } finally {
      setProcessing(false);
    }
  }, [threadId, agentId, activeOrganization, processing]);

  // Resume listening helper
  const resumeListening = useCallback(() => {
    if (autoListen && callActive && recognitionRef.current) {
      setTimeout(() => {
        try {
          recognitionRef.current.start();
        } catch (e) {
          // Ignore if already started
        }
      }, 500);
    }
  }, [autoListen, callActive]);

  // NOTE: speakText is defined after speakWithOpenAI and speakWithBrowser due to dependencies

  // ═══════════════════════════════════════════════════════════════════════════
  // STOP ALL AUDIO: Ensure single playback pipeline
  // ═══════════════════════════════════════════════════════════════════════════
  const stopAllAudio = useCallback(() => {
    // Stop HTML5 audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
    }
    // Stop browser speech synthesis
    if (browserSpeechSynthesis) {
      browserSpeechSynthesis.cancel();
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // OpenAI TTS - With RETRY logic and strict locked settings
  // isRetry: boolean to prevent infinite retry loops
  // ═══════════════════════════════════════════════════════════════════════════
  const speakWithOpenAI = useCallback(async (text, settings, isRetry = false) => {
    if (!text || !settings) {
      console.error('[VoiceTestMode] speakWithOpenAI called with missing text or settings');
      resumeListening();
      return;
    }
    
    // CRITICAL: Use ONLY the passed settings - never fall back to state
    const voiceToUse = settings.voice;
    const speedToUse = settings.speed;
    const utteranceNum = ++utteranceCountRef.current;
    
    // Stop any current playback before starting new
    stopAllAudio();
    
    setIsSpeaking(true);
    
    // Create debug info for this utterance
    const debugInfo = {
      utteranceNum,
      tts_provider: 'openai',
      voice_id: voiceToUse,
      speed: speedToUse,
      fallback_used: false,
      is_retry: isRetry,
      error_code: null,
      timestamp: new Date().toISOString()
    };
    
    console.log(`[VoiceTestMode] TTS Utterance #${utteranceNum}:`, debugInfo);
    
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/voice/tts`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            text,
            voice: voiceToUse,
            speed: speedToUse,
            model: 'tts-1',
            agent_id: agentId
          })
        },
        activeOrganization?.organization_id
      );
      
      if (!response.ok) {
        const errorCode = `HTTP_${response.status}`;
        throw new Error(errorCode);
      }
      
      // Get audio blob and play it
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.onended = () => {
          setIsSpeaking(false);
          URL.revokeObjectURL(audioUrl);
          // Update debug with success
          setLastUtteranceDebug({ ...debugInfo, status: 'success' });
          resumeListening();
        };
        audioRef.current.onerror = (e) => {
          console.error(`[VoiceTestMode] Audio playback error for utterance #${utteranceNum}:`, e);
          setIsSpeaking(false);
          URL.revokeObjectURL(audioUrl);
          setLastUtteranceDebug({ ...debugInfo, status: 'playback_error', error_code: 'AUDIO_PLAYBACK' });
          resumeListening();
        };
        await audioRef.current.play();
      }
    } catch (err) {
      console.error(`[VoiceTestMode] OpenAI TTS error for utterance #${utteranceNum}:`, err);
      const errorCode = err.message || 'UNKNOWN';
      
      // Update debug info with error
      debugInfo.error_code = errorCode;
      setLastUtteranceDebug({ ...debugInfo, status: 'error' });
      
      // ═══════════════════════════════════════════════════════════════════════════
      // RETRY LOGIC: Retry once before showing fallback option
      // ═══════════════════════════════════════════════════════════════════════════
      if (!isRetry) {
        console.log(`[VoiceTestMode] TTS failed, retrying once... (utterance #${utteranceNum})`);
        setError('TTS failed, retrying...');
        
        // Wait 500ms then retry
        setTimeout(() => {
          setError(null);
          speakWithOpenAI(text, settings, true); // Retry with same settings
        }, 500);
        return;
      }
      
      // Retry also failed - show visible fallback option
      setIsSpeaking(false);
      setTtsFallbackUsed(true);
      setLastUtteranceDebug({ 
        ...debugInfo, 
        status: 'failed_after_retry', 
        fallback_used: true 
      });
      setError(`OpenAI TTS failed after retry (${errorCode}). Voice output unavailable.`);
      resumeListening();
    }
  }, [agentId, activeOrganization, stopAllAudio]);

  // Browser TTS - Speak text (uses ONLY passed settings)
  const speakWithBrowser = useCallback((text, settings) => {
    if (!browserSpeechSynthesis || !text || !settings) {
      resumeListening();
      return;
    }
    
    // CRITICAL: Use ONLY the passed settings
    const voiceToUse = settings.browserVoice;
    const speedToUse = settings.speed;
    const utteranceNum = ++utteranceCountRef.current;
    
    // Stop any current playback before starting new
    stopAllAudio();
    
    // Create debug info
    const debugInfo = {
      utteranceNum,
      tts_provider: 'browser',
      voice_id: voiceToUse?.name || 'default',
      speed: speedToUse,
      fallback_used: settings.provider === 'openai', // True if we wanted OpenAI but using browser
      error_code: null,
      timestamp: new Date().toISOString()
    };
    
    console.log(`[VoiceTestMode] Browser TTS Utterance #${utteranceNum}:`, debugInfo);
    setLastUtteranceDebug(debugInfo);
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.voice = voiceToUse;
    utterance.rate = speedToUse;
    utterance.pitch = 1.0;
    
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => {
      setIsSpeaking(false);
      setLastUtteranceDebug({ ...debugInfo, status: 'success' });
      resumeListening();
    };
    utterance.onerror = (e) => {
      console.error(`[VoiceTestMode] Browser TTS error for utterance #${utteranceNum}:`, e);
      setIsSpeaking(false);
      setLastUtteranceDebug({ ...debugInfo, status: 'error', error_code: e.error || 'UNKNOWN' });
      setError('Browser TTS failed.');
      resumeListening();
    };
    
    browserSpeechSynthesis.speak(utterance);
  }, [stopAllAudio]);

  // ═══════════════════════════════════════════════════════════════════════════
  // SPEAK TEXT - wrapper function (uses LOCKED settings during call)
  // NOTE: Must be defined after speakWithOpenAI and speakWithBrowser
  // ═══════════════════════════════════════════════════════════════════════════
  const speakText = useCallback((text) => {
    if (!voiceEnabled || !text) {
      resumeListening();
      return;
    }
    
    // CRITICAL: Use ref for locked settings to avoid stale closure issues
    const settings = lockedTtsSettingsRef.current;
    
    if (!settings) {
      console.error('[VoiceTestMode] ERROR: speakText called without locked settings!');
      // Fallback to current settings only if somehow called outside a call
      const fallbackSettings = {
        provider: ttsProvider,
        voice: openaiVoice,
        browserVoice: selectedBrowserVoice,
        speed: ttsSpeed,
        available: ttsAvailable
      };
      if (fallbackSettings.provider === 'openai' && fallbackSettings.available) {
        speakWithOpenAI(text, fallbackSettings, false);
      } else {
        speakWithBrowser(text, fallbackSettings);
      }
      return;
    }
    
    // Use ONLY locked settings from ref - this ensures voice consistency
    console.log('[VoiceTestMode] Using LOCKED settings for TTS (from ref):', settings);
    
    if (settings.provider === 'openai' && settings.available) {
      speakWithOpenAI(text, settings, false);
    } else {
      speakWithBrowser(text, settings);
    }
  }, [voiceEnabled, ttsProvider, ttsAvailable, openaiVoice, selectedBrowserVoice, ttsSpeed, speakWithOpenAI, speakWithBrowser, resumeListening]);

  // Initialize speech recognition
  const startListening = useCallback(() => {
    if (!SpeechRecognition) {
      setError('Speech recognition not supported in this browser. Please use Chrome or Edge.');
      return;
    }
    
    // Stop any existing recognition
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // Ignore
      }
    }
    
    const recognition = new SpeechRecognition();
    recognition.continuous = false; // Changed to false for better control
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.maxAlternatives = 1;
    
    let finalTranscript = '';
    
    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
      finalTranscript = '';
      setCurrentTranscript('');
    };
    
    recognition.onresult = (event) => {
      let interim = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }
      
      // Update display
      setCurrentTranscript(finalTranscript + interim);
      transcriptRef.current = finalTranscript;
    };
    
    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'no-speech') {
        // No speech detected - restart if call is active
        setIsListening(false);
        if (callActive && autoListen) {
          setTimeout(() => startListening(), 1000);
        }
      } else if (event.error === 'aborted') {
        // Aborted - don't show error
        setIsListening(false);
      } else {
        setError(`Speech recognition error: ${event.error}`);
        setIsListening(false);
      }
    };
    
    recognition.onend = () => {
      setIsListening(false);
      const transcript = transcriptRef.current.trim();
      
      if (transcript && callActive) {
        // Process the transcript
        handleUserMessage(transcript);
        setCurrentTranscript('');
        transcriptRef.current = '';
      } else if (callActive && autoListen && !processing && !isSpeaking) {
        // Restart listening if no transcript but call is still active
        setTimeout(() => startListening(), 500);
      }
    };
    
    recognitionRef.current = recognition;
    
    try {
      recognition.start();
    } catch (e) {
      console.error('Failed to start recognition:', e);
    }
  }, [callActive, autoListen, handleUserMessage, processing, isSpeaking]);

  // Stop listening
  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // Ignore
      }
    }
    setIsListening(false);
  }, []);

  // Toggle listening
  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  // Start the test call
  const startCall = useCallback(() => {
    setCallActive(true);
    setCallDuration(0);
    setMessages([]);
    setThreadId(null);
    setError(null);
    setCurrentTranscript('');
    transcriptRef.current = '';
    setTtsFallbackUsed(false); // Reset fallback flag
    setLastUtteranceDebug(null); // Reset debug info
    utteranceCountRef.current = 0; // Reset utterance counter
    
    // Stop any lingering audio from previous session
    stopAllAudio();
    
    // ═══════════════════════════════════════════════════════════════════════════
    // LOCK TTS SETTINGS: Snapshot current TTS config at call start
    // This ensures consistent voice throughout the entire conversation
    // ═══════════════════════════════════════════════════════════════════════════
    const lockedSettings = {
      provider: ttsProvider,
      voice: openaiVoice,
      browserVoice: selectedBrowserVoice,
      speed: ttsSpeed,
      available: ttsAvailable
    };
    setLockedTtsSettings(lockedSettings);
    lockedTtsSettingsRef.current = lockedSettings; // Keep ref in sync
    console.log('[VoiceTestMode] ═══════════════════════════════════════════════════');
    console.log('[VoiceTestMode] SESSION STARTED - TTS Settings LOCKED:');
    console.log('[VoiceTestMode]   Provider:', lockedSettings.provider);
    console.log('[VoiceTestMode]   Voice:', lockedSettings.voice);
    console.log('[VoiceTestMode]   Speed:', lockedSettings.speed);
    console.log('[VoiceTestMode]   OpenAI Available:', lockedSettings.available);
    console.log('[VoiceTestMode] ═══════════════════════════════════════════════════');
    
    // Send initial greeting
    setMessages([{
      role: 'assistant',
      content: welcomeMessage,
      timestamp: new Date().toISOString()
    }]);
    
    // Speak welcome message using locked settings
    if (voiceEnabled) {
      if (lockedSettings.provider === 'openai' && lockedSettings.available) {
        speakWithOpenAI(welcomeMessage, lockedSettings, false);
      } else {
        speakWithBrowser(welcomeMessage, lockedSettings);
      }
    } else if (autoListen) {
      // If voice disabled but auto-listen enabled, start listening after a delay
      setTimeout(() => startListening(), 500);
    }
    // Otherwise wait for user to click mic button
  }, [welcomeMessage, voiceEnabled, ttsProvider, ttsAvailable, openaiVoice, selectedBrowserVoice, ttsSpeed, speakWithOpenAI, speakWithBrowser, startListening, stopAllAudio, autoListen]);

  // End the test call
  const endCall = useCallback(() => {
    setCallActive(false);
    setIsListening(false);
    
    // ═══════════════════════════════════════════════════════════════════════════
    // UNLOCK TTS SETTINGS: Clear locked config when call ends
    // ═══════════════════════════════════════════════════════════════════════════
    setLockedTtsSettings(null);
    lockedTtsSettingsRef.current = null; // Clear ref too
    setTtsFallbackUsed(false);
    setLastUtteranceDebug(null);
    console.log('[VoiceTestMode] ═══════════════════════════════════════════════════');
    console.log('[VoiceTestMode] SESSION ENDED - TTS Settings UNLOCKED');
    console.log('[VoiceTestMode] Total utterances:', utteranceCountRef.current);
    console.log('[VoiceTestMode] ═══════════════════════════════════════════════════');
    
    // Stop recognition
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // Ignore
      }
    }
    
    // Stop all audio using unified function
    stopAllAudio();
    setIsSpeaking(false);
    
    // Add end message
    setMessages(prev => [...prev, {
      role: 'system',
      content: 'Call ended',
      timestamp: new Date().toISOString()
    }]);
  }, [stopAllAudio]);

  // Format duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Reset conversation
  const resetConversation = () => {
    setMessages([]);
    setThreadId(null);
    setCurrentTranscript('');
    transcriptRef.current = '';
    setError(null);
  };

  // Save welcome message
  const saveWelcomeMessage = () => {
    setWelcomeMessage(tempWelcomeMessage);
    setEditingWelcome(false);
  };

  // Start editing welcome message
  const startEditingWelcome = () => {
    setTempWelcomeMessage(welcomeMessage);
    setEditingWelcome(true);
  };

  // Handle Realtime mode messages
  const handleRealtimeMessage = useCallback((msg) => {
    setMessages(prev => [...prev, {
      ...msg,
      timestamp: new Date().toISOString()
    }]);
  }, []);

  // Handle Realtime mode state changes
  const handleRealtimeStateChange = useCallback((state) => {
    console.log('[VoiceTestMode] Realtime state:', state);
    if (state === 'connected') {
      setCallActive(true);
    } else if (state === 'disconnected' || state === 'error') {
      setCallActive(false);
    }
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      {/* Hidden audio element for OpenAI TTS playback */}
      <audio ref={audioRef} className="hidden" />
      
      <div className="bg-[#1A1B1E] rounded-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden shadow-2xl">
        {/* Header - Compact */}
        <div className="flex items-center justify-between p-3 border-b border-[#2D3748] shrink-0">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center ${callActive ? 'bg-green-500 animate-pulse' : 'bg-[#00E5C0]'}`}>
              <Phone className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-white font-semibold text-sm">Voice Test - {agentName}</h2>
              <p className="text-[#718096] text-xs">{businessName}</p>
            </div>
          </div>
          
          {/* Status Badges */}
          <div className="flex items-center gap-2">
            {/* Connection Status */}
            <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
              callActive ? 'bg-green-500/20 text-green-400' : 'bg-[#2D3748] text-[#718096]'
            }`}>
              {callActive ? <WifiHigh size={12} /> : <WifiSlash size={12} />}
              {callActive ? 'Connected' : 'Ready'}
            </div>
            
            {/* Mode Badge */}
            <div className={`px-2 py-1 rounded text-xs ${
              voiceMode === VOICE_MODE.REALTIME 
                ? 'bg-[#00E5C0]/20 text-[#00E5C0]' 
                : 'bg-blue-500/20 text-blue-400'
            }`}>
              {voiceMode === VOICE_MODE.REALTIME ? 'Realtime' : 'Streaming'}
            </div>
            
            <button
              onClick={onClose}
              className="p-1.5 text-[#A0AEC0] hover:text-white hover:bg-[#2D3748] rounded-lg transition-colors"
              title="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Two-Pane Layout */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Pane - GearSix (Fixed Width) */}
          <div className="w-80 shrink-0 border-r border-[#2D3748] flex flex-col bg-[#0A0F1A]">
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Mode Selector */}
              <div>
                <label className="block text-xs text-[#718096] mb-2 uppercase tracking-wide">Mode</label>
                <div className="px-3 py-2 rounded-lg text-xs bg-[#00E5C0]/10 text-[#00E5C0] border border-[#00E5C0]/30 flex items-center gap-2">
                  <WifiHigh size={14} />
                  Realtime
                </div>
              </div>

              {/* Welcome Message */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs text-[#718096] uppercase tracking-wide">Welcome Message</label>
                  {!editingWelcome ? (
                    <button
                      onClick={startEditingWelcome}
                      disabled={callActive}
                      className="text-xs text-[#00E5C0] hover:underline disabled:opacity-50"
                    >
                      PencilSimple
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button onClick={saveWelcomeMessage} className="text-xs text-green-400 hover:underline">Save</button>
                      <button onClick={() => setEditingWelcome(false)} className="text-xs text-red-400 hover:underline">Cancel</button>
                    </div>
                  )}
                </div>
                {editingWelcome ? (
                  <textarea
                    value={tempWelcomeMessage}
                    onChange={(e) => setTempWelcomeMessage(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 bg-[#1A1B1E] border border-[#2D3748] rounded-lg text-white text-xs resize-none"
                    placeholder="Enter welcome message..."
                  />
                ) : (
                  <div className="bg-[#1A1B1E] px-3 py-2 rounded-lg border border-[#2D3748]">
                    <p className="text-xs text-gray-400 line-clamp-3">{welcomeMessage}</p>
                  </div>
                )}
              </div>

              {/* Voice Selection */}
              <div>
                <label className="block text-xs text-[#718096] mb-2 uppercase tracking-wide">Voice</label>
                {voiceMode === VOICE_MODE.REALTIME ? (
                  <select
                    value={realtimeVoice}
                    onChange={(e) => setRealtimeVoice(e.target.value)}
                    disabled={callActive}
                    className="w-full px-3 py-2 bg-[#1A1B1E] border border-[#2D3748] rounded-lg text-white text-xs disabled:opacity-50"
                  >
                    {OPENAI_REALTIME_VOICES.map(voice => (
                      <option key={voice.id} value={voice.id}>{voice.name} - {voice.description}</option>
                    ))}
                  </select>
                ) : (
                  <select
                    value={openaiVoice}
                    onChange={(e) => setOpenaiVoice(e.target.value)}
                    disabled={callActive}
                    className="w-full px-3 py-2 bg-[#1A1B1E] border border-[#2D3748] rounded-lg text-white text-xs disabled:opacity-50"
                  >
                    {OPENAI_VOICES.map(voice => (
                      <option key={voice.id} value={voice.id}>{voice.name} - {voice.description}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Pause Detection (Realtime only) */}
              {voiceMode === VOICE_MODE.REALTIME && (
                <div>
                  <label className="block text-xs text-[#718096] mb-2 uppercase tracking-wide">
                    Pause Detection: {(pauseTolerance / 1000).toFixed(1)}s
                  </label>
                  <input
                    type="range"
                    min="1000"
                    max="3000"
                    step="100"
                    value={pauseTolerance}
                    onChange={(e) => setPauseTolerance(parseInt(e.target.value))}
                    disabled={callActive}
                    className="w-full disabled:opacity-50"
                  />
                </div>
              )}

              {/* Speed (Streaming only) */}
              {voiceMode === VOICE_MODE.STREAMING && (
                <div>
                  <label className="block text-xs text-[#718096] mb-2 uppercase tracking-wide">
                    Speed: {ttsSpeed.toFixed(1)}x
                  </label>
                  <input
                    type="range"
                    min="0.5"
                    max="2"
                    step="0.1"
                    value={ttsSpeed}
                    onChange={(e) => setTtsSpeed(parseFloat(e.target.value))}
                    disabled={callActive}
                    className="w-full disabled:opacity-50"
                  />
                </div>
              )}

              {/* Toggles */}
              <div className="space-y-3 pt-2 border-t border-[#2D3748]">
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-xs text-[#A0AEC0]">Voice Output</span>
                  <input
                    type="checkbox"
                    checked={voiceEnabled}
                    onChange={(e) => setVoiceEnabled(e.target.checked)}
                    disabled={callActive}
                    className="form-checkbox h-4 w-4 text-[#00E5C0] rounded border-[#2D3748] bg-[#1A1B1E]"
                  />
                </label>
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <span className="text-xs text-[#A0AEC0]">Auto-Listen</span>
                    <p className="text-[10px] text-[#718096]">Microphone starts automatically after AI speaks</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={autoListen}
                    onChange={(e) => setAutoListen(e.target.checked)}
                    disabled={callActive}
                    className="form-checkbox h-4 w-4 text-[#00E5C0] rounded border-[#2D3748] bg-[#1A1B1E]"
                  />
                </label>
              </div>
            </div>

            {/* Start/End Button - Sticky Bottom */}
            <div className="p-4 border-t border-[#2D3748] bg-[#0A0F1A]">
              {voiceMode === VOICE_MODE.STREAMING ? (
                <button
                  onClick={callActive ? endCall : startCall}
                  disabled={processing}
                  className={`w-full py-3 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2 ${
                    callActive
                      ? 'bg-red-500 hover:bg-red-600 text-white'
                      : 'bg-[#00E5C0] hover:bg-[#5558E3] text-white'
                  } disabled:opacity-50`}
                >
                  {callActive ? (
                    <>
                      <PhoneDisconnect size={16} />
                      End Call
                    </>
                  ) : (
                    <>
                      <Phone size={16} />
                      Start Call
                    </>
                  )}
                </button>
              ) : (
                /* Realtime mode button is inside RealtimeVoiceSession */
                <div className="text-center text-xs text-[#718096]">
                  Use controls in conversation area →
                </div>
              )}
            </div>
          </div>

          {/* Right Pane - Conversation/Transcript */}
          <div className="flex-1 flex flex-col overflow-hidden bg-[#1A1B1E]">
            {/* Conversation Area - Scrollable */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Realtime Mode */}
              {voiceMode === VOICE_MODE.REALTIME && realtimeAvailable && (
                <>
                  {/* Welcome screen - shown before call starts */}
                  {!callActive && messages.length === 0 && (
                    <div className="py-6 text-center">
                      <div className="w-14 h-14 rounded-full bg-[#00E5C0]/20 flex items-center justify-center mx-auto mb-4">
                        <WifiHigh className="w-7 h-7 text-[#00E5C0]" />
                      </div>
                      <h3 className="text-white font-semibold mb-2">Realtime Voice Ready</h3>
                      <p className="text-[#A0AEC0] text-sm max-w-xs mx-auto">
                        Low-latency WebRTC connection. Speak naturally.
                      </p>
                      <p className="text-[#00E5C0] text-xs mt-2">
                        ✓ OpenAI Realtime API ({realtimeKeySource || 'org'} key)
                      </p>
                    </div>
                  )}
                  
                  {/* Messages */}
                  {messages.length > 0 && (
                    <div className="space-y-3">
                      {messages.map((msg, idx) => (
                        <div
                          key={idx}
                          className={`flex ${msg.role === 'user' ? 'justify-end' : msg.role === 'system' ? 'justify-center' : 'justify-start'}`}
                        >
                          {msg.role === 'system' ? (
                            <span className="text-xs text-[#718096] bg-[#2D3748]/50 px-3 py-1 rounded-full">
                              {msg.content}
                            </span>
                          ) : (
                            <div className={`max-w-[85%] p-3 rounded-2xl ${
                              msg.role === 'user' 
                                ? 'bg-[#00E5C0] text-white rounded-br-md' 
                                : 'bg-[#2D3748] text-white rounded-bl-md'
                            }`}>
                              <p className="text-sm">{msg.content}</p>
                              <span className="text-xs opacity-60 mt-1 block">
                                {new Date(msg.timestamp).toLocaleTimeString()}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* RealtimeVoiceSession Component */}
                  <div className="mt-4">
                    <RealtimeVoiceSession
                      agentId={agentId}
                      agentName={agentName}
                      businessName={businessName}
                      businessDescription={businessDescription}
                      customKnowledge={customKnowledge}
                      requiredBookingInfo={requiredBookingInfo}
                      voice={realtimeVoice}
                      welcomeMessage={welcomeMessage}
                      pauseTolerance={pauseTolerance}
                      onMessage={handleRealtimeMessage}
                      onError={(err) => setError(err?.message || 'Realtime error')}
                      onStateChange={handleRealtimeStateChange}
                    />
                  </div>
                </>
              )}
              
              {/* Streaming Mode */}
              {voiceMode === VOICE_MODE.STREAMING && (
                <>
                  {/* Welcome screen */}
                  {messages.length === 0 && !callActive && (
                    <div className="py-6 text-center">
                      <div className="w-14 h-14 rounded-full bg-[#00E5C0]/20 flex items-center justify-center mx-auto mb-4">
                        <Microphone className="w-7 h-7 text-[#00E5C0]" />
                      </div>
                      <h3 className="text-white font-semibold mb-2">Ready to Test</h3>
                      <p className="text-[#A0AEC0] text-sm max-w-xs mx-auto">
                        Configure settings in the left panel, then start the call.
                      </p>
                      {ttsAvailable && (
                        <p className="text-green-400 text-xs mt-2">
                          ✓ OpenAI TTS available ({ttsKeySource} key)
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Messages */}
                  {messages.length > 0 && (
                    <div className="space-y-3">
                      {messages.map((msg, idx) => (
                        <div
                          key={idx}
                          className={`flex ${msg.role === 'user' ? 'justify-end' : msg.role === 'system' ? 'justify-center' : 'justify-start'}`}
                        >
                          {msg.role === 'system' ? (
                            <span className="text-xs text-[#718096] bg-[#2D3748]/50 px-3 py-1 rounded-full">
                              {msg.content}
                            </span>
                          ) : (
                            <div className={`max-w-[85%] p-3 rounded-2xl ${
                              msg.role === 'user' 
                                ? 'bg-[#00E5C0] text-white rounded-br-md' 
                                : 'bg-[#2D3748] text-white rounded-bl-md'
                            }`}>
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                              <span className="text-xs opacity-60 mt-1 block">
                                {new Date(msg.timestamp).toLocaleTimeString()}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            
            {/* Streaming Mode Controls - Bottom Bar */}
            {voiceMode === VOICE_MODE.STREAMING && callActive && (
              <div className="p-4 border-t border-[#2D3748] bg-[#0A0F1A]">
                {/* Status Indicators - Fixed position above buttons */}
                <div className="flex justify-center gap-2 mb-3 h-6">
                  {isListening && (
                    <div className="flex items-center gap-2 px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                      Listening...
                    </div>
                  )}
                  {isSpeaking && (
                    <div className="flex items-center gap-2 px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs">
                      <SpeakerHigh size={14} />
                      Speaking...
                    </div>
                  )}
                </div>
                {/* Buttons - Fixed position */}
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={toggleListening}
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                      isListening 
                        ? 'bg-green-500 text-white animate-pulse' // Active/listening = green
                        : 'bg-[#2D3748] text-white hover:bg-[#3D4758]' // Muted = gray
                    }`}
                    title={isListening ? 'Mute (Stop Listening)' : 'Unmute (Start Listening)'}
                  >
                    {isListening ? <Microphone size={20} /> : <MicrophoneSlash size={20} />}
                  </button>
                  <button
                    onClick={endCall}
                    className="w-12 h-12 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
                    title="End Call"
                  >
                    <PhoneDisconnect size={20} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Error Toast */}
        {error && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-red-500/90 text-white rounded-lg text-sm flex items-center gap-2 shadow-lg">
            <WarningCircle size={16} />
            {error}
            <button onClick={() => setError(null)} className="ml-2 hover:text-red-200">
              <X size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceTestMode;
